import { ReactNode, useContext } from "react";

import ChatIcon from "@mui/icons-material/Chat";
import { Alert, Box, Chip, Link, Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router";

import { mailTo } from "@/common/utils";
import AppCard from "@/components/dashboard/AppCard";
import styles from "@/components/dashboard/AppSelector.module.scss";
import { AuthStateContext } from "@/contexts/AuthContext";

import { RubiMessageAccess } from "./useRubiMessageAccess";

const RUBIMESSAGE_DEMO_URL =
  "https://www.loom.com/share/886423d7737e4b568abf662ddb1ca215";

const DESCRIPTION =
  "Send report links, template messages, two-way SMS, and more from one place.";

const DEMO_EMAIL_BODY = `Hello Rubicon Team,

I'd like to learn more about RubiMESSAGE. I've filled out some information below to help you prepare for the conversation.

Agency:
Name & Title / Rank:
Desk phone:
Cell phone:

Challenges we're looking to solve:

Why we're looking to make a change now:

Timeline or deadlines to be aware of:

Rubicon solutions we're interested in:

How we heard about Rubicon:

Anything else that would be helpful to know:

Thank you.

---------------------
After we receive your email, a member of the Rubicon team will reach out to coordinate a call. We look forward to connecting!`;

interface RubiMessageDiscoveryBannerProps {
  access: RubiMessageAccess;
  // E.164 number (e.g. "+16471112233") to open a conversation with, if valid.
  phoneNumber: string | null;
}

const RubiMessageDiscoveryBanner = ({
  access,
  phoneNumber,
}: RubiMessageDiscoveryBannerProps) => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthStateContext);

  const openRubiMessage = () => {
    navigate(
      phoneNumber
        ? `/rubimessage/${currentUser?.rubi_org_id}/conversation/${phoneNumber}`
        : "/rubimessage",
    );
  };

  const demoLink = (
    <Link href={RUBIMESSAGE_DEMO_URL} target="_blank" rel="noopener noreferrer">
      Watch a 3-minute demo
    </Link>
  );

  let title: ReactNode;
  let body: ReactNode;
  let footer: ReactNode = demoLink;

  if (access === "user") {
    title = "You already have access to RubiMESSAGE";
    body = (
      <>
        {DESCRIPTION}{" "}
        <Link component="button" variant="body2" onClick={openRubiMessage}>
          <b>Open RubiMESSAGE</b>
        </Link>
      </>
    );
  } else if (access === "org") {
    title = "RubiMESSAGE is already available at your organization";
    body = (
      <>
        Ask your supervisor for access or contact{" "}
        <Link
          rel="noopener noreferrer"
          target="_blank"
          href={mailTo({
            email: "support@tryrubicon.com",
            subject: "Requesting Access to RubiMESSAGE",
            body: `Hello Rubicon Support Team,

I would like to request access to RubiMESSAGE.

Thank you.

Sincerely,

${currentUser?.first_name} ${currentUser?.last_name}
${currentUser?.email}`,
          })}
        >
          <b>support@tryrubicon.com</b>
        </Link>
        . {DESCRIPTION}
      </>
    );
  } else {
    title = (
      <>
        <Chip label="NEW" size="small" color="info" sx={{ mr: 1 }} />
        Check out RubiMESSAGE
      </>
    );
    body = DESCRIPTION;
    footer = (
      <>
        {demoLink} - or reach out to{" "}
        <Link
          rel="noopener noreferrer"
          target="_blank"
          href={mailTo({
            email: "demo@tryrubicon.com",
            subject: "RubiMESSAGE demo request",
            body: DEMO_EMAIL_BODY,
          })}
        >
          <b>demo@tryrubicon.com</b>
        </Link>{" "}
        for an in-depth walkthrough.
      </>
    );
  }

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 2,
      }}
    >
      <Box sx={{ width: { xs: "100%", sm: 300 }, flexShrink: 0 }}>
        <AppCard
          title="RubiMESSAGE"
          description="Message System"
          icon={<ChatIcon className={styles.icon} fontSize="large" />}
          disabled={access !== "user"}
          onClick={openRubiMessage}
        />
      </Box>
      <Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
        >
          {title}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {body}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {footer}
        </Typography>
      </Box>
    </Box>
  );

  if (access === "org") {
    return (
      <Alert
        severity="info"
        icon={false}
        sx={{ mt: 2.5, "& .MuiAlert-message": { width: "100%" } }}
      >
        {content}
      </Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2.5,
        p: 2,
        bgcolor: access === "none" ? "background.default" : "background.paper",
      }}
    >
      {content}
    </Paper>
  );
};

export default RubiMessageDiscoveryBanner;
